import Button from '@components/Button';
import MainHeader from '@components/EligibilityV2/MainHeader';
import ResetButton from '@components/EligibilityV2/ResetButton';
import FormOptionWrapper from '@components/FormOptionWrapper';
import Header from '@components/Header';
import InputWrapper, { InputName } from '@components/Input';
import { ActionType, useEligibility } from '@contexts/eligibility';
import { yupResolver } from '@hookform/resolvers/yup';
import { PetType, Sex } from '@prisma/client';
import { ELIGIBLE_CAT_BREED, nameRegex } from '@utils/constant';
import { Months } from '@utils/date';
import { gtm } from '@utils/gtm';
import { getDaysInMonth } from 'date-fns';
import dayjs from 'dayjs';
import { useErrorAutoscroll } from 'hooks/useErrorAutoscroll';
import { GetStaticProps } from 'next';
import { TFunction, useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';

const schema = (t: TFunction, currentMonth: number, currentYear: number) =>
  yup.object({
    petName: yup
      .string()
      .required(t('enter_valid_name'))
      .matches(
        nameRegex,
        'Please make sure the name only contains alphabetical letters'
      ),
    petSex: yup.string().nullable().required(t('choose_gender')),
    petBreed: yup
      .string()
      .required(t('choose_breed'))
      .min(1, t('choose_breed')),
    petYear: yup
      .number()
      .required(t('choose_birth_year'))
      .transform((val) => (Number.isNaN(val) ? undefined : val))
      .max(currentYear, t('invalid_year_max', { year: currentYear }))
      .min(currentYear - 10, t('invalid_year_min', { year: currentYear - 10 })),
    petMonth: yup.number().required(t('choose_birth_month')).min(1).max(12),
    petDay: yup
      .number()
      .required(t('choose_birth_day'))
      .min(1, t('day_more_than_0'))
      .max(
        getDaysInMonth(
          new Date(`${currentYear}-${String(currentMonth).padStart(2, '0')}-01`)
        ),
        t('day_cnt_exceed', {
          day_cnt: getDaysInMonth(
            new Date(
              `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`
            )
          ),
        })
      )
      .nullable()
      .typeError(t('choose_birth_day')),
    exactDate: yup.boolean().nullable(),
  });

const getPetBreeds = () => {
  const petBreed: string[] = [];

  petBreed.push(...Object.values(ELIGIBLE_CAT_BREED));

  return petBreed.sort();
};

const getTranslatedMonth = (dateStr: string) => {
  // remove all numbers and special characters
  const date = dateStr.replace(/[^a-zA-Z]/g, '').toLocaleLowerCase();
  return `months.${date}`;
};

const getMonths = Object.keys(Months).map((label: string) => ({
  label,
  value: Months[label].toString(),
}));

const getYears = Array.from(
  { length: 11 },
  (_, i) => new Date().getFullYear() - i
);

const PetDetails: React.FC = () => {
  const router = useRouter();
  const referralCode = router.query.promo;
  const { t: tErr } = useTranslation('error');
  const { t: tc } = useTranslation('common');
  const { t: tpc } = useTranslation('eligibilityV2');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [currentMonth, setCurrentMonth] = useState<number>(dayjs().month() + 1);
  const {
    dispatch: eligibilityDispatch,
    state: { data: eligibilityData },
  } = useEligibility();
  const {
    setFocus,
    register,
    setValue,
    handleSubmit,
    getValues,
    clearErrors,
    formState: { errors },
    watch,
  } = useForm({
    criteriaMode: 'all',
    mode: 'all',
    resolver: yupResolver(schema(tErr, currentMonth, new Date().getFullYear())),
    defaultValues: {
      ...eligibilityData,
      petYear: eligibilityData.petDateOfBirth
        ? dayjs(eligibilityData?.petDateOfBirth).year()
        : undefined,
      petMonth: eligibilityData.petDateOfBirth
        ? dayjs(eligibilityData?.petDateOfBirth).month() + 1
        : undefined,
      petDay: eligibilityData.petDateOfBirth
        ? dayjs(eligibilityData?.petDateOfBirth).date()
        : undefined,
      exactDate:
        eligibilityData.petDateOfBirth &&
        dayjs(eligibilityData?.petDateOfBirth).date()
          ? true
          : false,
      petBreed: 'Kucing Kampung',
    },
  });

  useErrorAutoscroll(errors, setFocus);

  const [watchExactDate] = watch(['exactDate']);

  const [involveAsia, setIACookie] = useCookies(['involve_asia']);

  useEffect(() => {
    const queryString = window.location.search;
    if (
      typeof window !== 'undefined' &&
      !involveAsia.involve_asia &&
      queryString.length > 0
    ) {
      const urlParams = new URLSearchParams(queryString);
      const utmSource = urlParams.get('utm_source');
      const utmMedium = urlParams.get('utm_medium');
      const utmClickId = urlParams.get('click_id');
      if (queryString.length > 0) {
        setIACookie(
          'involve_asia',
          JSON.stringify({
            utm_source: utmSource,
            utm_medium: utmMedium,
            clickId: utmClickId,
          }),
          {
            path: '/',
            maxAge: 60 * 60 - 60 * 5,
            sameSite: false,
          }
        );
      }
    }

    // temp fix for clearing create password email being sent. Refactor to be done
    if (window.localStorage.getItem('createPasswordSent')) {
      window.localStorage.clear();
    }
  }, []);

  useEffect(() => {
    if (!watchExactDate) {
      setValue('petDay', 1);
    }
  }, [watchExactDate]);

  const onSubmit: SubmitHandler<{
    petBreed?: string;
    petName?: string;
    petType?: PetType;
    petSex?: string;
    petYear?: number;
    petMonth?: number;
    petDay?: number | null;
    exactDate?: boolean;
  }> = async (payload: {
    petBreed?: string;
    petName?: string;
    petType?: PetType;
    petSex?: string;
    petYear?: number;
    petMonth?: number;
    petDay?: number | null;
    exactDate?: boolean;
  }) => {
    const petDateOfBirth = `${payload.petYear}-${`0${payload.petMonth}`.slice(
      -2
    )}-${`${payload.petDay ? payload.petDay : '01'}`}`;

    payload.petType = PetType.CAT;

    setIsLoading(true);
    await Promise.all([
      eligibilityDispatch({
        type: ActionType.Next,
        payload: {
          ...payload,
          petDateOfBirth,
          productCode: 'zurich-takaful',
        },
      }),
      eligibilityDispatch({
        type: ActionType.Save,
      }),
    ]);

    gtm.track('pet_info_submitted', {
      pet_type: payload.petType,
      pet_name: payload.petName,
      pet_gender: payload.petSex,
      pet_breed: payload.petBreed,
      pet_DOB: petDateOfBirth,
      source: 'signup-lite',
    });

    router.push(
      `/eligibility/takaful/userDetails${
        typeof referralCode !== 'undefined' ? `?promo=${referralCode}` : ''
      }`
    );
  };

  return (
    <div className="flex flex-col items-center justify-center align-middle bg-white p-2 w-full">
      <div className="w-full space-y-4 mt-4 md:w-[500px]">
        <MainHeader
          title={tpc('pet_details_main_header_title')}
          description={tc('get_you_signed_up')}
        />
        <div className="mb-2">
          <Header title={tpc('pet_details_header_title')} />
        </div>
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <InputWrapper
            label={tpc('common_pet_name')}
            inputProps={{
              name: 'petName',
              input: InputName.Text,
              setValue,
              value: getValues('petName'),
              register,
            }}
            errors={errors}
          />
          <p className="pb-2">{tpc('common_pet_gender')}</p>
          <FormOptionWrapper
            inputProps={{
              name: 'petSex',
              input: 'radio',
              setValue,
              register,
              value: getValues('petSex'),
              options: [
                {
                  label: tc('male'),
                  value: Sex.MALE,
                },
                {
                  label: tc('female'),
                  value: Sex.FEMALE,
                },
              ],
            }}
            errors={errors}
          />
          <InputWrapper
            label={tpc('common_pet_breed')}
            inputProps={{
              name: 'petBreed',
              input: InputName.Select,
              value: getValues('petBreed'),
              register,
              setValue,
              clearErrors,
              options: getPetBreeds().map((breed: string) => ({
                label: breed,
                value: breed,
              })),
            }}
            errors={errors}
          />
          <p className="pb-2">{tpc('pet_birthday')}</p>
          <div className="flex flex-row space-x-4">
            {watchExactDate && (
              <InputWrapper
                label={tc('day_capital')}
                className="w-[160px]"
                inputProps={{
                  name: 'petDay',
                  input: InputName.Number,
                  value: getValues('petDay') || '1',
                  setValue,
                  register,
                }}
                errors={errors}
              />
            )}
            <InputWrapper
              label={tc('month_capital')}
              className={watchExactDate ? 'w-[160px]' : 'w-[50%]'}
              inputProps={{
                onChange: (e) => setCurrentMonth(e),
                name: 'petMonth',
                input: InputName.Select,
                clearErrors,
                value: getValues('petMonth')?.toString(),
                setValue,
                register,
                options: getMonths.map((month) => ({
                  label: tc(getTranslatedMonth(month.label)),
                  value: month.value,
                })),
              }}
              errors={errors}
            />
            <InputWrapper
              label={tc('year_capital')}
              className={watchExactDate ? 'w-[160px]' : 'w-[50%]'}
              inputProps={{
                name: 'petYear',
                input: InputName.Select,
                clearErrors,
                value: getValues('petYear'),
                setValue,
                register,
                options: getYears.map((year) => ({
                  label: year.toString(),
                  value: year.toString(),
                })),
              }}
              errors={errors}
            />
          </div>
          <InputWrapper
            inputProps={{
              name: 'exactDate',
              input: InputName.Checkbox,
              checkboxLabel: tpc('know_exact_date'),
              value: getValues('exactDate'),
              setValue,
              clearErrors,
              register,
            }}
            errors={errors}
          />
          <footer className="actions">
            <Button submit disabled={isLoading} className="flex-grow">
              {isLoading ? '...' : tc('next')}
            </Button>
          </footer>
        </form>
        <div className="flex flex-row items-center justify-center">
          <div>{tc('already_have_an_account')}</div>
          <Link href="/dashboard/auth/sign_in">
            <button type="button" className="link ml-1">
              {tc('sign_in_here')}
            </button>
          </Link>
        </div>
        <p className="text-gray-600 text-xs text-center">
          {tc('oyen_is_authorized_distributor_of')}
        </p>
      </div>
      <ResetButton flow={'takaful'} />
    </div>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale = '' }) => ({
  props: {
    ...(await serverSideTranslations(locale, [
      'dashboard',
      'planCard',
      'common',
      'eligibilityV2',
      'error',
    ])),
  },
});

export default PetDetails;
